<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import { SocialProvider } from '@xcel-next/rendering-tools/types/constants'

import { useConfigStore } from '../../../stores/config'
import { usePageStore } from '../../../stores/page'
import AiGeneratedBadge from '../AiGeneratedBadge.vue'
import PinterestShareButton from '../PinterestShareButton.vue'

import type { ImageParagraph } from '../../../utils/paragraph-schema/paragraphs'

type ImageProps = NonNullable<ImageParagraph['image']>

const props = withDefaults(defineProps<{
  image: ImageProps,
  isPreview?: boolean,
  listOrderPosition?: number,
  target?: ImageProps['target'],
  rel?: ImageProps['rel']
}>(), {
  isPreview: false,
  listOrderPosition: -1
})

const { rsConfig } = storeToRefs(useConfigStore())
const { pageData, isArticleOrArticleVersion } = storeToRefs(usePageStore())

const showPinterestShareBadge = computed(() => {
  return !props.isPreview &&
    !!rsConfig.value.socialSettings?.socialShare?.article?.includes(SocialProvider.Pinterest) &&
    props.listOrderPosition === 0 &&
    isArticleOrArticleVersion.value &&
    (!pageData.value?.metaData?.advertorial || (pageData.value?.metaData?.advertorial && pageData.value?.metaData?.hideAdvertorial))
})

const imageRel = computed(() => props.image.rel || props.rel)
const imageTarget = computed(() => props.image.target || props.target)
const isAiGenerated = computed(() => props.image?.isAiGenerated)

const postFormatImageDescription = (html?: string): string =>
  html?.length ? html.replace(/<\/?(p|h2|h3|br)(.|\n)*?>/gi, ' ') : ''
</script>

<template>
  <figure>
    <a v-if="!!image.externalUrl?.length"
       :href="image.externalUrl"
       :rel="imageRel"
       :target="imageTarget">
      <slot name="sourceSet" />
    </a>
    <slot v-else
          name="sourceSet" />
    <PinterestShareButton v-if="showPinterestShareBadge"
                          type="badge" />
    <AiGeneratedBadge v-if="isAiGenerated" />
    <figcaption class="bx-typo--content-continuous-text">
      <span v-if="!!image.imageDescription?.html?.length"
            class="bx-content__media-caption bx-content__media-img-description"
            data-tc="image-description"
            v-html="postFormatImageDescription(image.imageDescription.html)" />
      <span v-if="!!image.imageCredit?.length"
            class="bx-content__media-credit bx-content__media-img-credit"
            data-tc="image-credit">
        Foto: {{ image.imageCredit }}
      </span>
    </figcaption>
  </figure>
</template>
